import { useEffect } from "react";
import { Navigate } from "react-router-dom";

import { Icon } from "~/assets/icons/Icon";
import { useCardDialog } from "~/components/autopto/cardDialog/useCardDialog";
import { UPSELL_VIDEO_URL } from "~/components/autopto/consts";
import {
  dueAutoptoOffer,
  isAutoptoTrial,
} from "~/components/autopto/isAutoptoTrial";
import LazyVideo from "~/components/autopto/welcome/LazyVideo";
import Button, { UnstyledButton } from "~/components/mantine/button/Button";
import { AlertCard } from "~/components/ui/card/AlertCard";
import PendingStatus from "~/components/ui/pendingStatus/PendingStatus";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import { useTeam } from "~/contexts/TeamProvider";
import { scrollMainToTop } from "~/helpers/scrollMainToTop";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./AutoptoOfferPage.module.scss";
import signature from "./kyle-signature.png";
import { offerContent } from "./offerContent";
import { OfferKey, useOfferPurchase } from "./useOfferPurchase";

const OfferSymbol = ({ bonus = false }: { bonus?: boolean }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={bonus ? 2.5 : 3.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {bonus ? (
      <>
        <rect x="3" y="8" width="18" height="13" rx="1" />
        <path d="M12 8v13M3 12h18M12 8S10 3 7.5 4.5 9 8 12 8zM12 8s2-5 4.5-3.5S15 8 12 8z" />
      </>
    ) : (
      <path d="M20 6 9 17l-5-5" />
    )}
  </svg>
);

const OfferView = ({ offerKey }: { offerKey: OfferKey }) => {
  const { user } = useAuth();
  const { team } = useTeam();
  const { openCardDialog, isOpeningCardDialog, isAwaitingCard } =
    useCardDialog();
  const { action, error, purchase, decline } = useOfferPurchase(offerKey);

  // The downsell takes the upsell's place: from its top.
  useEffect(() => {
    scrollMainToTop();
  }, []);

  const content = offerContent(offerKey, {
    firstName: user?.first_name || null,
    platformName: team?.platform_name || null,
  });
  const last4 = team?.subscription?.payment_method?.data?.last4;
  // Another card holds the retry until its webhook lands.
  const isLocked = action !== null || isAwaitingCard;
  // The offer stays due past the trial, if the page is reopened.
  const isTrial = isAutoptoTrial(team);

  return (
    <div className={styles.page} data-offer={offerKey}>
      <p className={styles.step}>
        {isTrial && (
          <span className={styles.trialStatus}>
            <OfferSymbol />
            Your 7-day trial is active
          </span>
        )}
        <span>{content.step}</span>
      </p>
      <header className={styles.hero}>
        <span className={styles.eyebrow}>{content.eyebrow}</span>
        <h1 className={styles.headline}>{content.headline}</h1>
        <ul className={styles.highlights}>
          {content.highlights.map((highlight, index) => (
            <li key={index} className={styles.highlight}>
              <span className={styles.highlightIcon}>
                <OfferSymbol />
              </span>
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      </header>
      <section className={styles.card}>
        <div className={styles.letter}>
          {content.hasVideo && (
            <div className={styles.video}>
              <LazyVideo
                src={UPSELL_VIDEO_URL}
                caption="A quick word from Kyle"
                playLabel="Play Kyle's video"
                autoplay
                loop={false}
                onWatch={() => {}}
              />
            </div>
          )}
          {content.letter.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
          {content.isSigned && (
            <p className={styles.signature}>
              Cheers,
              <br />
              <img src={signature} alt="Kyle" width="170" />
            </p>
          )}
        </div>
        <div className={styles.product}>
          <div className={styles.productHeader}>
            <span className={styles.eyebrow}>{content.productEyebrow}</span>
            <h2 className={styles.productName}>{content.productName}</h2>
            <p className={styles.productDescription}>
              {content.productDescription}
            </p>
          </div>
          <h3 className={styles.itemsTitle}>Here's everything you get</h3>
          <ul className={styles.items}>
            {content.items.map(({ title, description, price, isBonus }) => (
              <li key={title} className={styles.item}>
                <span
                  className={styles.itemIcon}
                  data-bonus={isBonus || undefined}
                >
                  <OfferSymbol bonus={isBonus} />
                </span>
                <div className={styles.itemText}>
                  <span className={styles.itemTitle}>
                    {isBonus && <span className={styles.bonus}>Bonus</span>}
                    {title}
                  </span>
                  <span className={styles.itemDescription}>{description}</span>
                </div>
                <span className={styles.itemPrice}>{price}</span>
              </li>
            ))}
          </ul>
          <div className={styles.credit}>
            <div className={styles.itemText}>
              <span className={styles.itemTitle}>{content.credit.title}</span>
              <span className={styles.itemDescription}>
                {content.credit.description}
              </span>
            </div>
            <span className={styles.itemPrice}>{content.credit.amount}</span>
          </div>
          <dl className={styles.summary}>
            <div className={styles.summaryRow}>
              <dt>Total value</dt>
              <dd>
                <s>{content.totalValue}</s>
              </dd>
            </div>
            <div className={styles.summaryRow}>
              <dt className={styles.priceLabel}>
                Your price today
                <span className={styles.savings}>{content.savings}</span>
              </dt>
              <dd className={styles.price}>{content.price}</dd>
            </div>
          </dl>
          {error && (
            <AlertCard
              type="danger"
              content={error.message}
              actions={
                error.canChangeCard && (
                  <Button
                    onClick={openCardDialog}
                    loading={isOpeningCardDialog}
                    disabled={isLocked}
                  >
                    Use Another Card
                  </Button>
                )
              }
            />
          )}
          <div className={styles.checkout}>
            <Button
              size="lg"
              fullWidth
              className={styles.accept}
              classNames={{ inner: styles.acceptInner }}
              onClick={purchase}
              disabled={isLocked}
              loading={action === "purchase"}
            >
              <span className={styles.acceptText}>
                <span className={styles.acceptLabel}>
                  {content.acceptLabel}
                </span>
                <span className={styles.acceptHint}>{content.acceptHint}</span>
              </span>
              <svg
                className={styles.acceptArrow}
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Button>
            {isAwaitingCard ? (
              <PendingStatus>Confirming your new card…</PendingStatus>
            ) : (
              <p className={styles.cardNote}>
                <Icon iconName="GreenLockIcon" />
                {last4
                  ? `One click, card ending ${last4}`
                  : "One click, with your saved card"}
              </p>
            )}
            <p className={styles.chargeNote}>
              {isTrial
                ? "Charged today. Your trial continues as normal."
                : "Charged today."}
            </p>
          </div>
          <div className={styles.guarantee}>
            <span className={styles.guaranteeBadge}>
              <strong>90</strong>
              days
            </span>
            <strong>90 Day No-Questions-Asked, Money Back Guarantee</strong>
          </div>
        </div>
      </section>
      <div className={styles.decline}>
        {action === "decline" ? (
          <PendingStatus>One moment…</PendingStatus>
        ) : (
          <UnstyledButton
            className={styles.declineButton}
            onClick={decline}
            disabled={isLocked}
          >
            {content.declineLabel}
          </UnstyledButton>
        )}
      </div>
    </div>
  );
};

/**
 * The one-time Autopto offer after the first card: the upsell, then the
 * downsell once it's declined, as long as the backend keeps one due (a
 * reload or a card change comes back to it). Without one, the dashboard.
 */
const AutoptoOfferPage = () => {
  const { team } = useTeam();
  const offerKey = dueAutoptoOffer(team);

  usePageTitle(
    offerKey === "autopto_downsell"
      ? PAGE_TITLES.autoptoDownsell
      : PAGE_TITLES.autoptoUpsell,
  );

  if (!offerKey) return <Navigate to={routes.home()} replace />;

  // Each offer starts over: no error carried from the other.
  return <OfferView key={offerKey} offerKey={offerKey} />;
};

export default AutoptoOfferPage;
